import { useRef } from 'react';
import { Button, Input } from 'reactstrap';
import {
  DailyScheduleEmployeeDTO,
  DailyScheduleEntryDTO,
  HighlightColor,
  StaffStatus,
} from '../../../../interfaces/DailyScheduleInterface';
import Time12Input from './Time12Input';

interface Props {
  entry: DailyScheduleEntryDTO;
  roomName: string;
  highlightColor: HighlightColor;
  employees: DailyScheduleEmployeeDTO[];
  onChange: (entryId: string, field: string, value: any) => void;
  onRemove: (entryId: string) => void;
}

const STATUS_OPTIONS: { value: StaffStatus; label: string }[] = [
  { value: 'ON_SCHEDULE', label: 'On Schedule' },
  { value: 'CLOCKED_IN', label: 'Clocked In' },
  { value: 'CALLED_OUT', label: 'Called Out' },
  { value: 'CLOSED', label: 'Closed' },
  { value: 'OUT_OFF', label: 'Out / Off' },
];

const STATUS_CLASS: Record<StaffStatus, string> = {
  ON_SCHEDULE: 'status--on-schedule',
  CLOCKED_IN: 'status--clocked-in',
  CALLED_OUT: 'status--called-out',
  CLOSED: 'status--closed',
  OUT_OFF: 'status--out-off',
};

const ScheduleEntryRow = ({ entry, roomName, highlightColor, employees, onChange, onRemove }: Props) => {
  const debounceRef = useRef<Record<string, ReturnType<typeof setTimeout>>>({});

  const handleChange = (field: string, value: any) => {
    onChange(entry.id, field, value);

    clearTimeout(debounceRef.current[field]);
    debounceRef.current[field] = setTimeout(() => {
      onChange(`__save__${entry.id}`, field, value);
    }, 300);
  };

  const colorClass = highlightColor ? `row--${highlightColor}` : '';

  return (
    <tr className={colorClass}>
      <td style={{ color: '#8898aa', fontSize: '0.78rem', whiteSpace: 'nowrap' }}>{roomName}</td>
      <td style={{ minWidth: 160 }}>
        <Input
          type="select"
          bsSize="sm"
          value={entry.employeeId ?? ''}
          onChange={(e) => handleChange('employeeId', e.target.value || null)}
        >
          <option value="">-- Select Staff --</option>
          {employees.map((emp) => (
            <option key={emp.id} value={emp.id}>
              {emp.user.firstName} {emp.user.lastName}
            </option>
          ))}
        </Input>
      </td>
      <td style={{ minWidth: 200 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
            <span style={{ fontSize: '0.68rem', color: '#8898aa', minWidth: 28, fontWeight: 600 }}>Shift</span>
            <Time12Input
              value={entry.scheduledTime || ''}
              onChange={(v) => handleChange('scheduledTime', v || null)}
              style={{ width: 90, fontSize: '0.82rem' }}
            />
            <span style={{ fontSize: '0.72rem', color: '#adb5bd' }}>–</span>
            <Time12Input
              value={entry.scheduledEndTime || ''}
              onChange={(v) => handleChange('scheduledEndTime', v || null)}
              style={{ width: 90, fontSize: '0.82rem' }}
            />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
            <span style={{ fontSize: '0.68rem', color: '#8898aa', minWidth: 28, fontWeight: 600 }}>Break</span>
            <Time12Input
              value={entry.lunchBreakTime || ''}
              onChange={(v) => handleChange('lunchBreakTime', v || null)}
              style={{ width: 90, fontSize: '0.82rem' }}
            />
            <span style={{ fontSize: '0.72rem', color: '#adb5bd' }}>–</span>
            <Time12Input
              value={entry.lunchBreakEndTime || ''}
              onChange={(v) => handleChange('lunchBreakEndTime', v || null)}
              style={{ width: 90, fontSize: '0.82rem' }}
            />
          </div>
        </div>
      </td>
      <td style={{ minWidth: 150 }}>
        <div className="d-flex align-items-center" style={{ flexWrap: 'wrap', gap: '0.3rem' }}>
          <Input
            type="select"
            bsSize="sm"
            value={entry.staffStatus}
            className={`status-select ${STATUS_CLASS[entry.staffStatus as StaffStatus] || ''}`}
            onChange={(e) => handleChange('staffStatus', e.target.value)}
          >
            {STATUS_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </Input>
          {entry.staffStatus !== 'CALLED_OUT' && (
            <Button
              color="danger"
              outline
              size="sm"
              style={{ fontSize: '0.7rem', padding: '0.15rem 0.5rem', whiteSpace: 'nowrap' }}
              title="Mark as Called Out"
              onClick={() => handleChange('staffStatus', 'CALLED_OUT')}
            >
              Call Out
            </Button>
          )}
        </div>
      </td>
      <td style={{ minWidth: 150 }}>
        <Input
          type="textarea"
          bsSize="sm"
          rows={2}
          value={entry.notes || ''}
          onChange={(e) => handleChange('notes', e.target.value || null)}
          placeholder="Notes..."
        />
      </td>
      <td style={{ width: 40, textAlign: 'center' }}>
        <Button
          color="link"
          size="sm"
          className="text-danger p-0"
          onClick={() => onRemove(entry.id)}
          title="Remove"
          style={{ fontSize: '1rem', lineHeight: 1 }}
        >
          ×
        </Button>
      </td>
    </tr>
  );
};

export default ScheduleEntryRow;
